import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface DirectoryLicense {
  id: string;
  license_type: string;
  license_number: string;
  license_state: string;
  expiration_date: string | null;
  is_primary: boolean | null;
}

export interface DirectoryAppraiser {
  id: string;
  user_id: string;
  name: string | null;
  slug: string | null;
  title: string | null;
  tagline: string | null;
  headshot_url: string | null;
  bio: string | null;
  about: string | null;
  certifications: string[] | null;
  specialties: string[] | null;
  years_experience: number | null;
  appraiser_since: number | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  city: string | null;
  state: string | null;
  license_state: string | null;
  brokerage_name: string | null;
  is_verified: boolean | null;
  company_name: string | null;
  company_logo_url: string | null;
  licenses: DirectoryLicense[];
  site_id: string | null;
  service_counties: string[] | null;
  accepting_trainees: boolean;
  supervisor_bio: string | null;
  supervisor_license_types: string[] | null;
  max_trainees: number | null;
}

const KNOWN_DESIGNATIONS = new Set([
  'MAI', 'SRA', 'AI-RRS', 'ASA', 'IFAS', 'RAA', 'AI-GRS', 'MRICS', 'CRE',
]);

export function getDesignations(certifications: string[] | null): string[] {
  if (!certifications) return [];
  return certifications.filter(c => KNOWN_DESIGNATIONS.has(c.toUpperCase()));
}

export function getNonDesignationCerts(certifications: string[] | null): string[] {
  if (!certifications) return [];
  return certifications.filter(c => !KNOWN_DESIGNATIONS.has(c.toUpperCase()));
}

interface UseDirectoryFilters {
  search: string;
  stateFilter: string;
  specialtyFilter: string[];
  licenseTypeFilter: string;
  countyFilter: string[];
  experienceFilter: string;
}

export function useDirectoryAppraisers(filters: UseDirectoryFilters) {
  const { data: rawData, isLoading, error } = useQuery({
    queryKey: ['directory-appraisers'],
    queryFn: async () => {
      // The directory intentionally fetches the full public roster once (cached
      // 5 min) and filters/enumerates facets in memory: the specialty/county/state
      // dropdowns need every option, and in-memory filtering keeps search + toggles
      // instant with no per-keystroke round trip. Server-side filtering is therefore
      // deliberately NOT used here. The one over-fetch worth cutting is the large
      // free-text `bio`/`about` columns — the cards never render them (only the
      // profile page loads them, via its own query), so they're omitted below.
      const { data: agents, error: agentsError } = await supabase
        .from('agents')
        .select(`
          id, user_id, name, slug, title, tagline, headshot_url,
          certifications, specialties, years_experience, appraiser_since, phone, email, website,
          city, state, license_state, brokerage_name, is_verified, company_id, service_counties,
          accepting_trainees, supervisor_bio, supervisor_license_types, max_trainees
        `)
        .eq('is_public', true)
        .order('name', { ascending: true });

      if (agentsError) throw agentsError;
      if (!agents || agents.length === 0) return [];

      const userIds = agents.map(a => a.user_id);
      const companyIds = agents.map(a => a.company_id).filter(Boolean) as string[];

      // Fetch licenses and companies in parallel
      const [licensesRes, companiesRes, sitesRes] = await Promise.all([
        supabase
          .from('appraiser_licenses')
          .select('id, user_id, license_type, license_number, license_state, expiration_date, is_primary')
          .in('user_id', userIds),
        companyIds.length > 0
          ? supabase.from('companies').select('id, name, logo_url').in('id', companyIds)
          : Promise.resolve({ data: [] as { id: string; name: string; logo_url: string | null }[] }),
        supabase
          .from('appraiser_sites')
          .select('id, user_id')
          .eq('is_published', true)
          .in('user_id', userIds),
      ]);

      const licenseMap = new Map<string, DirectoryLicense[]>();
      (licensesRes.data || []).forEach(l => {
        const existing = licenseMap.get(l.user_id) || [];
        existing.push(l);
        licenseMap.set(l.user_id, existing);
      });

      const companyMap = new Map<string, { name: string; logo_url: string | null }>();
      ((companiesRes as any).data || []).forEach((c: any) => {
        companyMap.set(c.id, { name: c.name, logo_url: c.logo_url });
      });

      const siteMap = new Map<string, string>();
      (sitesRes.data || []).forEach(s => {
        siteMap.set(s.user_id, s.id);
      });

      return agents.map(agent => {
        const company = agent.company_id ? companyMap.get(agent.company_id) : null;
        return {
          id: agent.id,
          user_id: agent.user_id,
          name: agent.name,
          slug: agent.slug,
          title: agent.title,
          tagline: agent.tagline,
          headshot_url: agent.headshot_url,
          bio: null,
          about: null,
          certifications: agent.certifications,
          specialties: agent.specialties,
          // bio/about are not loaded for the list (see query note); the profile
          // page fetches them on demand.
          years_experience: agent.years_experience,
          appraiser_since: (agent as any).appraiser_since ?? null,
          phone: agent.phone,
          email: agent.email,
          website: agent.website,
          city: agent.city,
          state: agent.state,
          license_state: agent.license_state,
          brokerage_name: agent.brokerage_name,
          is_verified: agent.is_verified,
          company_name: company?.name || null,
          company_logo_url: company?.logo_url || null,
          licenses: licenseMap.get(agent.user_id) || [],
          site_id: siteMap.get(agent.user_id) || null,
          service_counties: (agent as any).service_counties ?? null,
          accepting_trainees: (agent as any).accepting_trainees ?? false,
          supervisor_bio: (agent as any).supervisor_bio ?? null,
          supervisor_license_types: (agent as any).supervisor_license_types ?? null,
          max_trainees: (agent as any).max_trainees ?? null,
        } as DirectoryAppraiser;
      });
    },
    staleTime: 5 * 60 * 1000,
  });

  // Compute available specialties sorted by frequency
  const availableSpecialties = useMemo(() => {
    if (!rawData) return [];
    const frequencyMap = new Map<string, number>();
    rawData.forEach(a => {
      a.specialties?.forEach(s => {
        frequencyMap.set(s, (frequencyMap.get(s) || 0) + 1);
      });
    });
    return [...new Set(frequencyMap.keys())]
      .sort((a, b) => (frequencyMap.get(b) || 0) - (frequencyMap.get(a) || 0));
  }, [rawData]);

  // Compute available counties
  const availableCounties = useMemo(() => {
    if (!rawData) return [];
    const frequencyMap = new Map<string, number>();
    rawData.forEach(a => {
      a.service_counties?.forEach(c => {
        frequencyMap.set(c, (frequencyMap.get(c) || 0) + 1);
      });
    });
    return [...frequencyMap.keys()].sort();
  }, [rawData]);

  // Compute available states
  const availableStates = useMemo(() => {
    if (!rawData) return [];
    const states = new Set<string>();
    rawData.forEach(a => {
      if (a.state) states.add(a.state);
      a.licenses.forEach(l => {
        if (l.license_state) states.add(l.license_state);
      });
    });
    return [...states].sort();
  }, [rawData]);

  // Client-side filtering
  const filteredAppraisers = useMemo(() => {
    if (!rawData) return [];
    return rawData.filter(a => {
      // Text search
      if (filters.search) {
        const q = filters.search.toLowerCase();
        const searchable = [a.name, a.brokerage_name, a.company_name, a.city, a.state]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!searchable.includes(q)) return false;
      }

      // State filter
      if (filters.stateFilter && filters.stateFilter !== 'all') {
        const hasState = a.state === filters.stateFilter ||
          a.licenses.some(l => l.license_state === filters.stateFilter);
        if (!hasState) return false;
      }

      // License type filter
      if (filters.licenseTypeFilter && filters.licenseTypeFilter !== 'all') {
        const hasType = a.licenses.some(l =>
          l.license_type.toLowerCase().includes(filters.licenseTypeFilter.toLowerCase())
        );
        if (!hasType) return false;
      }

      // Specialty filter
      if (filters.specialtyFilter.length > 0) {
        const hasSpecialty = filters.specialtyFilter.some(sf =>
          a.specialties?.includes(sf)
        );
        if (!hasSpecialty) return false;
      }

      // County filter
      if (filters.countyFilter.length > 0) {
        const hasCounty = filters.countyFilter.some(cf =>
          a.service_counties?.includes(cf)
        );
        if (!hasCounty) return false;
      }

      // Experience filter
      if (filters.experienceFilter && filters.experienceFilter !== 'all') {
        const currentYear = new Date().getFullYear();
        const years = a.appraiser_since
          ? currentYear - a.appraiser_since
          : a.years_experience;
        if (years == null) return false;
        switch (filters.experienceFilter) {
          case '1-5': if (years < 1 || years >= 5) return false; break;
          case '5-10': if (years < 5 || years >= 10) return false; break;
          case '10-20': if (years < 10 || years >= 20) return false; break;
          case '20+': if (years < 20) return false; break;
        }
      }

      return true;
    });
  }, [rawData, filters]);

  return {
    appraisers: filteredAppraisers,
    totalCount: rawData?.length || 0,
    availableSpecialties,
    availableStates,
    availableCounties,
    isLoading,
    error,
  };
}
